import React, { FC, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { TYPE_WARNING } from '../../../shared/notification/actions';
import ModalDialog from '../../../shared/components/modal/ModalDialog';
import { textColor, THEME_PRIMARY } from '../../../styles/basics/colors';
import { BUTTON_SIZE_TYPES, SegmentedButton } from '../../../shared/common/SegmentedButton';
import { ReserveHint } from './ReserveHint';
import {
    COPY_FORMULA_CUSTOM,
    COPY_FORMULA_PROACTIVE,
    COPY_FORMULA_STANDARD,
    MAX_COPIES_UNLIMITED_KEY,
    MAX_COPIES_UNLIMITED_VALUE
} from '../constants';
import { ReserveListDefaultSettingsOut } from '../../../store/api';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { animateHintScroll } from './ReserveUtils';
import { ReserveReportSettings, ReserveReportSettingsState } from './ReserveReportSettings';
import { useTranslation } from 'react-i18next';
import { LOANFORMAT_EAUDIOBOOKS, LOANFORMAT_EBOOKS } from '../../../shared/loanFormat/constants';
import { showNotification } from '../../../shared/notification/toastNotification';

const StHeading = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 30px;
`;

const StSettingsTitle = styled.h2`
    && {
        ${textColor(THEME_PRIMARY)};
        margin-bottom: 0;
    }

    margin-right: 15px;
    font-size: 18px;
    padding-left: 0;
    line-height: 1;
`;

const StActionButtons = styled.div`
    display: flex;
    justify-content: space-between;
    flex-flow: row-reverse;
    flex-wrap: wrap;
`;

export const StCancelButton = styled(Button)`
    margin-right: 5px;
`;

type Props = {
    readonly show: boolean;
    readonly settings: ReserveListDefaultSettingsOut;
    readonly onSuccess: (settings: ReserveListDefaultSettingsOut) => void;
    readonly onAbort: () => void;
};

export type SupportedLoanFormat = typeof LOANFORMAT_EBOOKS | typeof LOANFORMAT_EAUDIOBOOKS;
export type FormulaKey = typeof COPY_FORMULA_STANDARD | typeof COPY_FORMULA_CUSTOM | typeof COPY_FORMULA_PROACTIVE;
export type MaxCopies = number | typeof MAX_COPIES_UNLIMITED_VALUE | typeof MAX_COPIES_UNLIMITED_KEY;

export const ReserveListDetailPopup: FC<Props> = props => {
    const { t } = useTranslation();
    const actionButtonRef = useRef<HTMLDivElement>(null);

    const [state, setState] = React.useState<ReserveReportSettingsState>({
        [LOANFORMAT_EBOOKS]: {
            copyFormula: COPY_FORMULA_STANDARD,
            maxCopies: MAX_COPIES_UNLIMITED_KEY,
            reserveRatio: 0,
            selected: false
        },
        [LOANFORMAT_EAUDIOBOOKS]: {
            copyFormula: COPY_FORMULA_STANDARD,
            maxCopies: MAX_COPIES_UNLIMITED_KEY,
            reserveRatio: 0,
            selected: false
        },
        editMode: false,
        isHintShown: false
    });

    useEffect(() => {
        setState(state => ({ ...state, ...(mapPropsToState(props) as ReserveReportSettingsState) }));
    }, [props]);

    useEffect(() => {
        if (state.isHintShown) {
            if (actionButtonRef.current) {
                animateHintScroll(actionButtonRef.current);
            }
        }
    }, [state.isHintShown]);

    function mapPropsToState(props: Props): Partial<ReserveReportSettingsState> {
        return {
            [LOANFORMAT_EBOOKS]: {
                copyFormula:
                    props.settings.loanFormatDefaults?.[LOANFORMAT_EBOOKS].copyFormula ?? COPY_FORMULA_STANDARD,
                maxCopies: props.settings.loanFormatDefaults?.[LOANFORMAT_EBOOKS].maxCopies ?? null,
                reserveRatio: props.settings.loanFormatDefaults?.[LOANFORMAT_EBOOKS].reserveRatio ?? 4,
                selected: props.settings.loanFormatDefaults?.[LOANFORMAT_EBOOKS].selected ?? false
            },
            [LOANFORMAT_EAUDIOBOOKS]: {
                copyFormula:
                    props.settings.loanFormatDefaults?.[LOANFORMAT_EAUDIOBOOKS].copyFormula ?? COPY_FORMULA_STANDARD,
                maxCopies: props.settings.loanFormatDefaults?.[LOANFORMAT_EAUDIOBOOKS].maxCopies ?? null,
                reserveRatio: props.settings.loanFormatDefaults?.[LOANFORMAT_EAUDIOBOOKS].reserveRatio ?? 4,
                selected: props.settings.loanFormatDefaults?.[LOANFORMAT_EAUDIOBOOKS].selected ?? false
            }
        };
    }

    function toggleHint() {
        setState(state => ({ ...state, isHintShown: !state.isHintShown }));
    }

    function onLoanformatCheckChange(checkState: boolean, loanFormat: SupportedLoanFormat) {
        setState(state => ({ ...state, [loanFormat]: { ...state[loanFormat], selected: checkState } }));
    }

    function onGenerate() {
        const settings = state.editMode ? { ...state } : { ...props.settings.loanFormatDefaults };
        const ebookMaxCopies = settings[LOANFORMAT_EBOOKS]?.maxCopies ?? 0;
        const eaudiobookMaxCopies = settings[LOANFORMAT_EAUDIOBOOKS]?.maxCopies;

        const generateSettings = {
            generatedWithDefaults: !state.editMode,
            loanFormatDefaults: {}
        };

        if (state.EBOOKS.selected) {
            if (ebookMaxCopies === MAX_COPIES_UNLIMITED_KEY) {
                settings[LOANFORMAT_EBOOKS].maxCopies = MAX_COPIES_UNLIMITED_VALUE;
            }
            generateSettings.loanFormatDefaults[LOANFORMAT_EBOOKS] = settings[LOANFORMAT_EBOOKS];
        }

        if (state.EAUDIOBOOKS.selected) {
            if (eaudiobookMaxCopies === MAX_COPIES_UNLIMITED_KEY) {
                settings[LOANFORMAT_EAUDIOBOOKS].maxCopies = MAX_COPIES_UNLIMITED_VALUE;
            }
            generateSettings.loanFormatDefaults[LOANFORMAT_EAUDIOBOOKS] = settings[LOANFORMAT_EAUDIOBOOKS];
        }

        if (isEmpty(generateSettings.loanFormatDefaults)) {
            showNotification(t('notifications.reserveListDetailPopup.onGenerate.warning'), TYPE_WARNING);
        } else {
            props.onSuccess(generateSettings);
        }
    }

    function abort() {
        setState(state => ({ ...state, editMode: false }));
        props.onAbort();
    }

    function enableEdit() {
        setState(state => ({ ...state, editMode: true }));
    }

    function disableEdit() {
        const state = mapPropsToState(props);
        setState({ ...state, editMode: false } as ReserveReportSettingsState);
    }

    function onSelectFormula(formulaKey: FormulaKey, loanFormat: SupportedLoanFormat) {
        setState(state => ({ ...state, [loanFormat]: { ...state[loanFormat], copyFormula: formulaKey } }));
    }

    function onSelectMaxCopies(maxCopies: MaxCopies, loanFormat: SupportedLoanFormat) {
        setState(state => ({ ...state, [loanFormat]: { ...state[loanFormat], maxCopies: maxCopies } }));
    }

    function onSelectReserveRatio(reserveRatio: number, loanFormat: SupportedLoanFormat) {
        setState(state => ({ ...state, [loanFormat]: { ...state[loanFormat], reserveRatio: reserveRatio } }));
    }

    return (
        <ModalDialog
            show={props.show}
            titleClassName="black-title"
            onCloseButtonClick={props.onAbort}
            title={t('reserveReports.generateNewReport.generateNewReport')}
            overwriteWidth={true}
            width={800}
            wrapClassName="reserve-settings-modal js-reserve-settings-height-resize"
            overwriteOverflowY={state.isHintShown ? 'auto' : 'visible'}
            overwriteOverflowX={state.isHintShown ? 'hidden' : 'visible'}
        >
            <StHeading>
                <StSettingsTitle>{t('reserveReports.generateNewReport.settings')}</StSettingsTitle>

                <SegmentedButton
                    size={BUTTON_SIZE_TYPES.BUTTON_SIZE_SMALL}
                    onClick={() => (state.editMode ? disableEdit() : enableEdit())}
                    options={[
                        { label: t('reserveReports.generateNewReport.default'), active: state.editMode },
                        { label: t('reserveReports.generateNewReport.custom'), active: !state.editMode }
                    ]}
                />
            </StHeading>

            <ReserveReportSettings
                onSelectMaxCopies={onSelectMaxCopies}
                onSelectReserveRatio={onSelectReserveRatio}
                onSelectFormula={onSelectFormula}
                onLoanformatCheckChange={onLoanformatCheckChange}
                state={state}
            />

            <StActionButtons ref={actionButtonRef}>
                <div>
                    <StCancelButton
                        size={ButtonSize.LARGE}
                        theme={ButtonTheme.POSITIVE_INVERT}
                        text={t('cancel')}
                        onClick={abort}
                    />
                    <Button
                        text={t('reserveReports.generateNewReport.generate')}
                        onClick={onGenerate}
                        size={ButtonSize.LARGE}
                    />
                </div>
                <ReserveHint
                    toggleHint={toggleHint}
                    isHintShown={state.isHintShown}
                    borderOverflow={'36px'}
                    lineHeight={'21px'}
                />
            </StActionButtons>
        </ModalDialog>
    );
};
